define((require) => {
    "use strict";

    const _                                         = require("underscore");
    const AbstractWidgetView                        = require("shared/ui/experience/widgets/AbstractWidgetView");
    const TextWidgetView                            = require("shared/ui/experience/widgets/textEnhancedWidget/TextWidgetView");
    const Marionette                                = require("marionette");
    const ReactBridge                               = require("backbone.react-bridge");
    const TextEnhancedWidget                        = require("shared/ui/experience/widgets/textEnhancedWidget/TextEnhancedComponent").default;
    const {ETW_IS_READY}                             = require("editor/ui/experience/widgets/textEnhancedWidget/textWidgetEvents");
    const EditorWebFontController                   = require("editor/ui/experience/editor/EditorWebFontController");
    const CharacterPropertiesViewControllerHelpers  = require("editor/ui/experience/widgets/CharacterPropertiesViewControllerHelpers");
    const NotificationBar                           = require("editor/ui/notificationbar/NotificationBar");

    const EnhancedTextWidgetLayout  = Marionette.Layout.extend({

        template: () => "<div class=\"text\"></div>",

        regions: {
            "text": ".text"
        }

    });

    /**
     * Mixin the editor related logic for the <tt>TextWidgetView</tt>.
     *
     * @see  TextWidgetView
     */
    return TextWidgetView.mixin("TextWidgetViewEditorMixin", {

        initialize() {
            // this is kind of hack
            // for some reason ReactBridge observer do not listen changes from experience model
            // so just trigger change
            this.listenTo(this.options.viewModel.get("experience"), "change:emailModeEnabled", () => {
                this.model.trigger("forceWidgetUpdate");
            });
        },

        _layout: null,

        className: `${AbstractWidgetView.prototype.className} text-enhanced`,

        // This gives us access to the proper this context
        getPropsFromView: (self) =>
            ({
                onChange: (data, fontFamilies, rawText) => {
                    // Using self as an argument is the only way we can access the proper context.
                    self.textEditorController._textWidgetModel.set({
                        text: data,
                        fontFamilies,
                        rawText
                    }, {render: false, shouldUpdateTabOrder: false});
                },
                onSelect: (event) => {
                    const {options} = self;
                    // For some reason the selection is not updated to reflect the state of the editor surface.
                    const selectedWidgets = options.viewModel.selection.get("widgets");
                    const singleSelection = selectedWidgets.length < 2; // Not a group selection
                    const modifierKeyDown = event.shiftKey || event.ctrlKey || event.metaKey; // Same keys as in FreeTransform.js#down

                    if (singleSelection) {
                        if (modifierKeyDown) {
                            const newSelectedWidgets = _.uniq([self].concat(selectedWidgets));
                            options.viewModel.selection.select(newSelectedWidgets);
                        } else {
                            options.viewModel.selection.select([self]);
                        }

                        self.model.trigger("forceWidgetUpdate");
                    }
                },
                onDeselect: (hasText) => {
                    if (!hasText) {
                        self.options.model.destroy();
                    }
                },
                onReady: () => {
                    self.options.viewModel.trigger(ETW_IS_READY);
                },
                onShowNotification: (msg, closeDelay = 5000) => {
                    NotificationBar.open(msg, {closeDelay});
                }
            }),

        modelEvents: {
            // renders are being prevented (see TextEditorController#saveModel) when editing a text
            // widget, however for text widget views (say, a representation of the an overlay on
            // another scene) it does need to update.
            // TODO: Method should be defined as a class method and called by a string to follow the style guide and for better readability,
            // but this causes the scope to be removed, possibly related to using mixins
            "content:updated": function() {
                const isBeingEdited = this.$el.attr("contenteditable") === "true";

                if (!isBeingEdited) {
                    this.render();
                }
            }
        },

        onRender() {
            this.contentCompatibilityCheck();

            const onRender = AbstractWidgetView.prototype.onRender;

            if (onRender) {
                onRender.apply(this, _.toArray(arguments));
            }
        },

        onShow() {
            this.__super();

            // Since abstractView defers, we also need to defer this in order to get correct width
            _.defer(() => {
                // Runs after showView, so that we can get correct width
                this.widthCompatibilityCheck();
            });
        },

        /**
         * Old widgets uses \n to break line, whereas new widgets need <br>
         */
        contentCompatibilityCheck() {
            this.model.set("text", this.model.get("text").replace("\n", "<br/>"));
        },

        widthCompatibilityCheck() {
            // Add width for the old experiences
            if (!this.model.has("width")) {
                const unscaledExperienceSize = this._experienceSize();

                let width = this.$el.width();

                if (width) {
                    // Legacy text widgets are rendered using different CSS
                    // settings and different fonts have different widths in
                    // different browsers. For this reason we give it some
                    // buffering to work with.
                    width += 10;

                    // Silently update the width (avoid re-rendering) and make sure to recompute any
                    // affected computed properties (hint: scaledWidth)
                    this.model.set({width: width / unscaledExperienceSize.width * 100}, {silent: true});
                    this.model.invalidateComputedProperties("width", true);
                }
            }
        },

        showView() {
            const rect = this.el.getBoundingClientRect();
            const invalidElement = rect.height === 0 && rect.width === 0;
            if (invalidElement) {
                // The Dynamic Product Widget is creating extra DOM nodes that can cause problems here.
                // The unwanted nodes have 0 for all properties in boundingClientRect.
                // Given that, we may reject the view if the element is invalid.
                return;
            }
            this.__super();
            EditorWebFontController.promise().then((results) => {
                CharacterPropertiesViewControllerHelpers._fontCollection = results;
                CharacterPropertiesViewControllerHelpers._fontSelectorQuery({
                    callback: (fonts) => {
                        this._layout = new EnhancedTextWidgetLayout({el: this.$el});
                        this._layout.render();

                        const scopedGetProps = ({
                            model, selection, viewModel, parentScene
                        }) => {
                            const selectionWidgets = selection && selection.get("widgets");
                            const viewModelSceneNumber = viewModel && viewModel.get("sceneNumber");
                            const parentSceneNumber = parentScene && parentScene.get("sceneNumber");
                            const isEmailMode = viewModel && viewModel.get("experience").get("emailModeEnabled");
                            const isWidgetInActiveScene = viewModelSceneNumber === parentSceneNumber;
                            return {
                                text: model.get("text"),
                                isSelected: isWidgetInActiveScene && selectionWidgets.some((widget) => widget.model === model),
                                readOnly: selection.isMultiple() || !isWidgetInActiveScene || model.get("isLocked"),
                                isTransforming: model.get("isTransforming"),
                                fonts,
                                uuid: model.get("uuid"),
                                hasAction: Boolean(model.get("action")),
                                isEmailMode: Boolean(isEmailMode)
                            };
                        };

                        const textEnhancedWidgetView = ReactBridge.viewFromComponent(TextEnhancedWidget, {
                            model: this.model,

                            viewModel:  this.options.viewModel,

                            selection:  this.options.viewModel.selection,

                            parentScene: this.parentView.scene || this.parentView.model.get("scene"),

                            observe: {
                                model: "change forceWidgetUpdate",
                                viewModel: "change",
                                selection: "change",
                                parentScene: "change"
                            },

                            // Passing in this so I have a reference to the backbone class.
                            props: this.getPropsFromView(this),

                            // The arguments here are from ReactBridge and NOT specifically our code.
                            // This is the model specified in the config object.
                            getProps: scopedGetProps
                        });

                        this._layout.text.show(textEnhancedWidgetView);
                    }
                });
            });
        },

        onClose() {
            if (this._layout) {
                this._layout.close();
            }
        }

    });
});



// WEBPACK FOOTER //
// ./src/editor/ui/experience/widgets/textEnhancedWidget/TextWidgetViewEditorMixin.js